import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Key, Trash2, Mail, User, Calendar, Gamepad2, Award, IdCard, Star } from 'lucide-react';
import { playClick, playSuccess } from '@/utils/sounds';
import AvatarCanvas, { AvatarCanvasWithLevel } from '@/components/AvatarCanvas';
import AvatarEditor from '@/components/AvatarEditor';
import BaseballCard from '@/components/BaseballCard';
import { RetroBackground, RetroButton, RetroPanel, PALETTE, DESIGN_TOKENS } from '@/components/RetroUI';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountPage() {
  const { user, logout, getAuthHeader, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [supportEmail, setSupportEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAvatarEditor, setShowAvatarEditor] = useState(false);
  const [showBaseballCard, setShowBaseballCard] = useState(false);
  
  // Change password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Delete account form
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, statsRes, supportRes] = await Promise.all([
          axios.get(`${API}/profile/me`, { headers: getAuthHeader() }),
          axios.get(`${API}/account/stats`, { headers: getAuthHeader() }),
          axios.get(`${API}/support-email`)
        ]);
        setProfile(profileRes.data);
        setStats(statsRes.data);
        setSupportEmail(supportRes.data.email);
      } catch (error) {
        console.error('Failed to fetch account data:', error);
      }
    };
    fetchData();
  }, [getAuthHeader]);

  const handleSaveAvatar = async (config) => {
    setLoading(true);
    try {
      await axios.put(`${API}/profile/avatar`, {
        gender: config.gender,
        hair_id: config.hair_id,
        primary_color: config.primary_color,
        secondary_color: config.secondary_color,
        equipped_flair: config.equipped_flair,
      }, { headers: getAuthHeader() });

      // Refresh profile data
      const profileRes = await axios.get(`${API}/profile/me`, { headers: getAuthHeader() });
      setProfile(profileRes.data);
      
      playSuccess();
      toast.success('Avatar updated!');
      setShowAvatarEditor(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save avatar');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    playClick();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/account/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers: getAuthHeader() });
      
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    playClick();
    
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/account/delete`, {
        password: deletePassword,
        confirmation: deleteConfirmation
      }, { headers: getAuthHeader() });
      
      toast.success('Account deleted. Goodbye!');
      logout();
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <RetroBackground variant="cream" className="p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <RetroButton
            variant="tertiary"
            size="sm"
            onClick={() => { playClick(); navigate('/dashboard'); }}
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft size={20} />
          </RetroButton>
          <h1 style={{
            fontFamily: DESIGN_TOKENS.fonts.display,
            fontSize: DESIGN_TOKENS.fontSizes.displayMd,
            color: PALETTE.ink,
            letterSpacing: '2px',
            textShadow: `2px 2px 0px rgba(0,0,0,0.15)`,
          }}>
            ACCOUNT
          </h1>
        </div>

        {/* File-folder tabs attached to content panel per reference */}
        <div 
          className="flex items-end overflow-x-auto"
          style={{ position: 'relative', zIndex: 2, paddingLeft: '8px', marginBottom: '-3px' }}
        >
          {['profile', 'avatar', 'password', 'support', 'delete'].map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => { playClick(); setActiveTab(tab); }}
                style={{
                  fontFamily: DESIGN_TOKENS.fonts.ui,
                  fontWeight: isActive ? 700 : 600,
                  fontSize: '13px',
                  padding: isActive ? '10px 14px 13px' : '8px 14px 10px',
                  whiteSpace: 'nowrap',
                  border: `3px solid ${PALETTE.ink}`,
                  borderBottom: isActive ? 'none' : `3px solid ${PALETTE.ink}`,
                  borderRadius: '10px 10px 0 0',
                  marginRight: '4px',
                  background: isActive ? PALETTE.cream : '#DDD3B8',
                  color: isActive ? PALETTE.ink : `${PALETTE.ink}99`,
                  transition: 'background 0.2s, color 0.2s',
                  cursor: 'pointer',
                }}
                data-testid={`tab-${tab}-btn`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            );
          })}
        </div>

        {/* Tab content panel */}
        <div
          style={{
            background: PALETTE.cream,
            border: `3px solid ${PALETTE.ink}`,
            borderRadius: '0 12px 12px 12px',
            padding: '16px',
            boxShadow: `5px 5px 0px rgba(0,0,0,0.25)`,
            position: 'relative',
            zIndex: 1,
          }}
        >

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Avatar & Level Display */}
            <div className="bg-white rounded-lg border-4 border-ink p-6 shadow-lg" data-testid="profile-section">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                {/* Avatar Preview */}
                <div className="flex-shrink-0">
                  {profile?.avatar_config && (
                    <AvatarCanvasWithLevel 
                      config={profile.avatar_config} 
                      level={profile.level || 1}
                      size={100}
                    />
                  )}
                </div>
                
                {/* User Info */}
                <div className="flex-1 text-center sm:text-left">
                  <h2 className="retro-heading text-xl text-ink mb-2">
                    {user?.username}
                  </h2>
                  
                  {/* XP Progress */}
                  <div className="mb-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="retro-text text-xs text-dirt">Level {profile?.level || 1}</span>
                      <span className="retro-text text-xs text-dirt">
                        {profile?.xp?.toLocaleString() || 0} XP
                      </span>
                    </div>
                    <div className="h-3 bg-ink border-2 border-ink overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-grass to-green-400 transition-all"
                        style={{ width: `${profile?.xp_progress_percent || 0}%` }}
                      />
                    </div>
                    <span className="retro-text text-xs text-dirt">
                      {profile?.xp_to_next_level || 100} XP to next level
                    </span>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="flex gap-2 justify-center sm:justify-start">
                    <button
                      onClick={() => { playClick(); setShowBaseballCard(true); }}
                      className="pixel-btn text-xs flex items-center gap-1"
                      data-testid="view-card-btn"
                    >
                      <IdCard size={14} />
                      VIEW CARD
                    </button>
                    <button
                      onClick={() => { playClick(); setActiveTab('avatar'); }}
                      className="pixel-btn text-xs flex items-center gap-1"
                      data-testid="edit-avatar-btn"
                    >
                      <User size={14} />
                      EDIT AVATAR
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Account Info */}
            <div className="bg-white rounded-lg border-4 border-ink p-6 shadow-lg">
              <h2 className="retro-heading text-ink text-lg mb-4">Account Info</h2>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-cream rounded-lg border-2 border-ink">
                  <Mail className="text-grass" size={20} />
                  <div>
                    <div className="retro-text text-xs text-ink/60">Email</div>
                    <div className="retro-text text-ink text-sm">{user?.email}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-cream rounded-lg border-2 border-ink">
                  <Calendar className="text-grass" size={20} />
                  <div>
                    <div className="retro-text text-xs text-ink/60">Member Since</div>
                    <div className="retro-text text-ink">{formatDate(stats?.member_since)}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-cream rounded-lg border-2 border-ink">
                  <Gamepad2 className="text-grass" size={20} />
                  <div>
                    <div className="retro-text text-xs text-ink/60">Games Played</div>
                    <div className="retro-text text-ink">{profile?.career_stats?.games_played ?? 0}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-cream rounded-lg border-2 border-ink">
                  <Award className="text-grass" size={20} />
                  <div>
                    <div className="retro-text text-xs text-ink/60">Record (W-L)</div>
                    <div className="retro-text text-ink">
                      {profile?.career_stats?.wins ?? 0} - {profile?.career_stats?.losses ?? 0}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Career Stats */}
            <div className="bg-white rounded-lg border-4 border-ink p-6 shadow-lg">
              <h2 className="retro-heading text-ink text-lg mb-4 flex items-center gap-2">
                <Star size={20} /> Career Stats
              </h2>
              
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className="retro-body text-xs text-dirt">1B</div>
                  <div className="retro-heading text-lg text-ink">{profile?.career_stats?.singles ?? 0}</div>
                </div>
                <div>
                  <div className="retro-body text-xs text-dirt">2B</div>
                  <div className="retro-heading text-lg text-ink">{profile?.career_stats?.doubles ?? 0}</div>
                </div>
                <div>
                  <div className="retro-body text-xs text-dirt">3B</div>
                  <div className="retro-heading text-lg text-ink">{profile?.career_stats?.triples ?? 0}</div>
                </div>
                <div>
                  <div className="retro-body text-xs text-dirt">HR</div>
                  <div className="retro-heading text-lg text-ink">{profile?.career_stats?.home_runs ?? 0}</div>
                </div>
                <div>
                  <div className="retro-body text-xs text-dirt">RBI</div>
                  <div className="retro-heading text-lg text-ink">{profile?.career_stats?.rbi ?? 0}</div>
                </div>
                <div>
                  <div className="retro-body text-xs text-dirt">RUNS</div>
                  <div className="retro-heading text-lg text-ink">{profile?.career_stats?.runs_scored ?? 0}</div>
                </div>
                <div>
                  <div className="retro-body text-xs text-dirt">OUTS</div>
                  <div className="retro-heading text-lg text-ink">{profile?.career_stats?.outs ?? 0}</div>
                </div>
                <div>
                  <div className="retro-body text-xs text-dirt">K</div>
                  <div className="retro-heading text-lg text-ink/50">
                    {profile?.career_stats?.strikeouts_pitched ?? '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Avatar Tab */}
        {activeTab === 'avatar' && (
          <div className="bg-white rounded-lg border-4 border-ink p-6 shadow-lg" data-testid="avatar-section">
            <h2 className="retro-heading text-ink text-lg mb-4 flex items-center gap-2">
              <User size={20} /> Customize Avatar
            </h2>
            
            <AvatarEditor
              initialConfig={profile?.avatar_config}
              onSave={handleSaveAvatar}
              onCancel={() => setActiveTab('profile')}
              showCancel={true}
              saveLabel="SAVE CHANGES"
              mode="full"
            />
          </div>
        )}

        {/* Change Password Tab */}
        {activeTab === 'password' && (
          <div className="bg-white rounded-lg border-4 border-ink p-6 shadow-lg" data-testid="password-section">
            <h2 className="retro-heading text-ink text-lg mb-4 flex items-center gap-2">
              <Key size={20} /> Change Password
            </h2>
            
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="retro-text text-ink text-sm block mb-1">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full p-3 border-2 border-ink rounded-lg retro-text bg-cream"
                  required
                  data-testid="current-password-input"
                />
              </div>
              
              <div>
                <label className="retro-text text-ink text-sm block mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full p-3 border-2 border-ink rounded-lg retro-text bg-cream"
                  required
                  minLength={8}
                  data-testid="new-password-input"
                />
                <p className="retro-text text-xs text-ink/60 mt-1">
                  Min 8 characters with uppercase, lowercase, and number
                </p>
              </div>
              
              <div>
                <label className="retro-text text-ink text-sm block mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full p-3 border-2 border-ink rounded-lg retro-text bg-cream"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-grass text-cream retro-text rounded-lg border-2 border-ink hover:bg-grass/80 transition-colors disabled:opacity-50"
                data-testid="change-password-btn"
              >
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
        )}

        {/* Support Tab */}
        {activeTab === 'support' && (
          <div className="bg-white rounded-lg border-4 border-ink p-6 shadow-lg" data-testid="support-section">
            <h2 className="retro-heading text-ink text-lg mb-4 flex items-center gap-2">
              <Mail size={20} /> Contact Support
            </h2>
            
            <p className="retro-text text-ink mb-4">
              Have a question, found a bug, or just want to say hi? We&apos;d love to hear from you!
            </p>
            
            <div className="bg-cream p-4 rounded-lg border-2 border-ink">
              <div className="retro-text text-xs text-ink/60 mb-1">Support Email</div>
              <a 
                href={`mailto:${supportEmail}?subject=Radio Baseball Support`}
                className="retro-text text-grass hover:underline text-lg"
                onClick={playClick}
                data-testid="support-email-link"
              >
                {supportEmail || 'Loading...'}
              </a>
            </div>
            
            <div className="mt-4 p-4 bg-grass/10 rounded-lg border-2 border-grass">
              <p className="retro-text text-ink text-sm">
                <strong>Tip:</strong> When reporting bugs, please include:
              </p>
              <ul className="retro-text text-ink text-sm mt-2 space-y-1 list-disc list-inside">
                <li>What you were trying to do</li>
                <li>What happened instead</li>
                <li>Your device and browser</li>
              </ul>
            </div>
          </div>
        )}

        {/* Delete Account Tab */}
        {activeTab === 'delete' && (
          <div className="bg-white rounded-lg border-4 border-red-500 p-6 shadow-lg" data-testid="delete-section">
            <h2 className="retro-heading text-red-600 text-lg mb-4 flex items-center gap-2">
              <Trash2 size={20} /> Delete Account
            </h2>
            
            <div className="bg-red-50 p-4 rounded-lg border-2 border-red-300 mb-4">
              <p className="retro-text text-red-800 text-sm">
                <strong>Warning:</strong> This action is permanent and cannot be undone.
              </p>
              <ul className="retro-text text-red-700 text-sm mt-2 space-y-1 list-disc list-inside">
                <li>Your account will be permanently deleted</li>
                <li>All your saved game history will be erased</li>
                <li>You will not be able to recover your data</li>
              </ul>
            </div>
            
            <form onSubmit={handleDeleteAccount} className="space-y-4">
              <div>
                <label className="retro-text text-ink text-sm block mb-1">Enter your password</label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="w-full p-3 border-2 border-ink rounded-lg retro-text bg-cream"
                  required
                  data-testid="delete-password-input"
                />
              </div>
              
              <div>
                <label className="retro-text text-ink text-sm block mb-1">
                  Type <span className="text-red-600 font-bold">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  className="w-full p-3 border-2 border-red-300 rounded-lg retro-text bg-cream"
                  placeholder="DELETE"
                  required
                  data-testid="delete-confirmation-input"
                />
              </div>
              
              <button
                type="submit"
                disabled={loading || deleteConfirmation !== 'DELETE'}
                className="w-full py-3 bg-red-600 text-white retro-text rounded-lg border-2 border-red-800 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="delete-account-btn"
              >
                {loading ? 'Deleting...' : 'Permanently Delete Account'}
              </button>
            </form>
          </div>
        )}
        </div>
      </div>

      {/* Baseball Card Modal */}
      {showBaseballCard && profile && (
        <BaseballCard 
          profile={profile} 
          onClose={() => setShowBaseballCard(false)} 
        />
      )}
    </RetroBackground>
  );
}
